// src/pages/lms/LMSFeed.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import { Plus, X, Megaphone, Trash2, Send } from 'lucide-react';

const CATEGORIES = ['general','academic','exam','holiday','event','sports','result','urgent','other'];
const PRIORITIES = ['normal','important','urgent'];

export default function LMSFeed() {
  const { account, institution } = useLMSAuth();
  const [posts,    setPosts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [form, setForm] = useState({ title:'', content:'', category:'general', priority:'normal', target_degree:'all', is_pinned:false });

  useEffect(() => { if (institution?.id) load(); }, [institution]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('lms_announcements')
        .select('*')
        .eq('institution_id', institution.id)
        .order('created_at', { ascending: false });
      setPosts(data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      // Insert into lms_announcements
      const { data: ann } = await supabase.from('lms_announcements').insert({
        institution_id:         institution.id,
        institution_account_id: account.id,
        title:           form.title.trim(),
        content:         form.content.trim(),
        category:        form.category,
        priority:        form.priority,
        target_degree:   form.target_degree,
        is_pinned:       form.is_pinned,
        is_published:    true,
        synced_to_app:   false,
      }).select().single();

      // Also post to campus_feed so it appears on the student app
      if (ann) {
        // Find institution's auth user to use as creator
        await supabase.from('campus_feed').insert({
          user_id:                account.auth_user_id,
          institution_id:         institution.id,
          content:                `📢 ${form.title}\n\n${form.content}`,
          feed_type:              'announcement',
          posted_by_institution:  true,
          institution_account_id: account.id,
        });

        // Mark as synced
        await supabase.from('lms_announcements')
          .update({ synced_to_app: true })
          .eq('id', ann.id);
      }

      await load();
      setShowForm(false);
      setForm({ title:'', content:'', category:'general', priority:'normal', target_degree:'all', is_pinned:false });
    } catch(e) { console.error(e); }
    finally { setSaving(false); }
  };

  const deletePost = async (id) => {
    await supabase.from('lms_announcements').update({ is_published: false }).eq('id', id);
    setPosts(prev => prev.filter(p => p.id !== id));
  };

  const priorityColor = p => ({ normal:'#4F8EF7', important:'#F59E0B', urgent:'#EF4444' })[p] || '#4F8EF7';
  const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box', outline:'none' };

  return (
    <LMSLayout>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'#111827', margin:0 }}>Post to Feed</h1>
          <p style={{ color:'#6B7280', fontSize:13, margin:'4px 0 0' }}>
            Posts appear on the student app home feed (visible only to your institution's students)
          </p>
        </div>
        <button onClick={() => setShowForm(true)}
          style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#F59E0B,#F97316)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
          <Plus size={16} /> New Post
        </button>
      </div>

      {/* Info banner */}
      <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:12, padding:'12px 16px', marginBottom:20, display:'flex', gap:10, alignItems:'flex-start' }}>
        <Megaphone size={16} color="#3B82F6" style={{ marginTop:1, flexShrink:0 }} />
        <div style={{ fontSize:13, color:'#1D4ED8', lineHeight:1.5 }}>
          Posts made here will instantly appear on the <strong>UNIVERSE app home feed</strong> for all students enrolled at your institution. Only your institution's students will see them.
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:48, color:'#9CA3AF' }}>Loading posts...</div>
      ) : posts.length === 0 ? (
        <div style={{ textAlign:'center', padding:48, background:'#fff', borderRadius:16, border:'1px solid #E5E7EB' }}>
          <Megaphone size={40} color="#E5E7EB" style={{ marginBottom:12 }} />
          <div style={{ fontSize:15, fontWeight:700, color:'#374151', marginBottom:6 }}>No posts yet</div>
          <div style={{ fontSize:13, color:'#9CA3AF' }}>Create your first announcement to reach your students</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {posts.map(post => (
            <div key={post.id} style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'hidden' }}>
              <div style={{ padding:'14px 20px', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                    {post.is_pinned && <span style={{ fontSize:10, fontWeight:800, color:'#4F8EF7', background:'#EFF6FF', padding:'2px 8px', borderRadius:4 }}>📌 PINNED</span>}
                    <span style={{ fontSize:10, fontWeight:800, color: priorityColor(post.priority), background:`${priorityColor(post.priority)}15`, padding:'2px 8px', borderRadius:4, textTransform:'uppercase' }}>
                      {post.priority}
                    </span>
                    <span style={{ fontSize:10, fontWeight:700, color:'#6B7280', background:'#F3F4F6', padding:'2px 8px', borderRadius:4, textTransform:'uppercase' }}>
                      {post.category}
                    </span>
                    {post.synced_to_app && <span style={{ fontSize:10, fontWeight:700, color:'#10B981', background:'#F0FDF4', padding:'2px 8px', borderRadius:4 }}>✓ On App</span>}
                  </div>
                  <div style={{ fontSize:15, fontWeight:800, color:'#111827', marginBottom:4 }}>{post.title}</div>
                  <div style={{ fontSize:13, color:'#6B7280', lineHeight:1.5 }}>{post.content}</div>
                </div>
                <button onClick={() => deletePost(post.id)}
                  style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'6px 8px', cursor:'pointer', flexShrink:0 }}>
                  <Trash2 size={14} color="#EF4444" />
                </button>
              </div>
              <div style={{ padding:'8px 20px', display:'flex', gap:16, fontSize:12, color:'#9CA3AF' }}>
                <span>Target: {post.target_degree === 'all' ? 'All Students' : post.target_degree}</span>
                <span>{new Date(post.created_at).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Post Modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:520, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
              <h3 style={{ margin:0, fontWeight:800, color:'#111827' }}>New Announcement</h3>
              <button onClick={() => setShowForm(false)} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={20} color="#6B7280" /></button>
            </div>
            <div style={{ padding:24 }}>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Title *</label>
                <input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="e.g. Exam Schedule Update" style={inp} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Content *</label>
                <textarea value={form.content} onChange={e=>setForm({...form,content:e.target.value})} placeholder="Write your announcement here..." rows={5}
                  style={{ ...inp, resize:'vertical' }} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Category</label>
                  <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})} style={inp}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Priority</label>
                  <select value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})} style={inp}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Target Students</label>
                <select value={form.target_degree} onChange={e=>setForm({...form,target_degree:e.target.value})} style={inp}>
                  <option value="all">All Students</option>
                  <option value="BS">BS Students</option>
                  <option value="BE">BE Students</option>
                  <option value="MS">MS Students</option>
                  <option value="MBA">MBA Students</option>
                  <option value="PhD">PhD Students</option>
                </select>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20, padding:'10px 14px', background:'#F9FAFB', borderRadius:8 }}>
                <input type="checkbox" id="pinned" checked={form.is_pinned} onChange={e=>setForm({...form,is_pinned:e.target.checked})} style={{ width:16, height:16 }} />
                <label htmlFor="pinned" style={{ fontSize:13, color:'#374151', fontWeight:600, cursor:'pointer' }}>
                  📌 Pin this announcement
                </label>
              </div>
              <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, padding:'10px 12px', marginBottom:16, fontSize:12, color:'#92400E' }}>
                This post will appear on the <strong>student app home feed</strong> immediately after posting.
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => setShowForm(false)}
                  style={{ flex:1, padding:'11px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={save} disabled={saving || !form.title.trim() || !form.content.trim()}
                  style={{ flex:2, padding:'11px 0', borderRadius:10, border:'none', background:'linear-gradient(135deg,#F59E0B,#F97316)', color:'#fff', fontWeight:700, cursor:'pointer', opacity:saving?0.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <Send size={14} /> {saving ? 'Posting...' : 'Post to Student Feed'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LMSLayout>
  );
}